import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CalendarIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { mealsAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';

interface MealPlan {
  id: string;
  name: string;
  description?: string;
  status: string;
  startDate: string;
  endDate: string;
  mealCount: number;
  completedDate?: string;
  createdAt: string;
}

type AccordionType = 'active' | 'completed' | null;

const ClientMeals: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [openAccordion, setOpenAccordion] = useState<AccordionType>('active');

  const { data, isLoading, error } = useQuery({
    queryKey: ['client-meal-plans'],
    queryFn: () => mealsAPI.getAll(),
    enabled: !!user,
  });

  const allPlans: MealPlan[] = data?.data?.mealPlans || [];

  // Helper to get all dates between two dates
  const getDatesBetween = (startDate: string, endDate: string) => {
    const dates = [];
    let curr = new Date(startDate.includes('T') ? startDate.split('T')[0] + 'T00:00:00' : startDate + 'T00:00:00');
    const last = new Date(endDate.includes('T') ? endDate.split('T')[0] + 'T00:00:00' : endDate + 'T00:00:00');
    
    while (curr <= last) {
      dates.push(new Date(curr));
      curr.setDate(curr.getDate() + 1);
    }
    return dates;
  };

  const activePlans = allPlans.filter(p => p.status !== 'COMPLETED' && p.status !== 'ARCHIVED');
  const completedPlans = allPlans.filter(p => p.status === 'COMPLETED');

  // Generate individual days for active plans
  const dailyMeals = activePlans.flatMap(plan => {
    const dates = getDatesBetween(plan.startDate, plan.endDate);
    return dates.map(date => ({
      date,
      plan,
      isToday: date.toDateString() === new Date().toDateString()
    }));
  }).sort((a, b) => b.date.getTime() - a.date.getTime()); // Newest first like workouts

  const toggleAccordion = (accordion: AccordionType) => {
    setOpenAccordion(openAccordion === accordion ? null : accordion);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading meal plans...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-red-600">Error loading meal plans</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 -mt-6">
      {/* Header */}
      <div className="sticky top-16 z-20 bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center space-x-2">
          <CalendarIcon className="h-5 w-5 text-green-600" />
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Your Meals</h1>
          </div>
        </div>
      </div>

      <div className="px-4 py-8 space-y-4">
        {/* Scheduled Meals Accordion */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => toggleAccordion('active')}
            className="w-full px-3 py-3 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <h3 className="text-base font-medium text-gray-900">Scheduled Meals</h3>
                <span className="bg-green-100 text-green-600 px-2 py-0.5 rounded-full text-xs font-medium">
                  {dailyMeals.length}
                </span>
              </div>
              {openAccordion === 'active' ? (
                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
              )}
            </div>
          </button>

          {openAccordion === 'active' && (
            <div className="border-t border-gray-200">
              {dailyMeals.length === 0 ? (
                <div className="p-4 text-center">
                  <CalendarIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <h3 className="text-sm font-medium text-gray-900">No scheduled meals</h3>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {dailyMeals.map((item, idx) => (
                    <div
                      key={`${item.plan.id}-${idx}`}
                      className={`p-3 hover:bg-gray-50 cursor-pointer transition-colors ${item.isToday ? 'bg-green-50/30' : ''}`}
                      onClick={() => navigate(`/client/meals/${item.plan.id}`)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                           <h4 className={`font-medium text-sm ${item.isToday ? 'text-green-700' : 'text-gray-900'}`}>
                             {item.plan.name}
                           </h4>
                           {item.isToday && (
                             <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-bold uppercase">Today</span>
                           )}
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatDate(item.date)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Completed Plans Accordion */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => toggleAccordion('completed')}
            className="w-full px-3 py-3 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <h3 className="text-base font-medium text-gray-900">Past Plans</h3>
                <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs font-medium">
                  {completedPlans.length}
                </span>
              </div>
              {openAccordion === 'completed' ? (
                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
              )}
            </div>
          </button>

          {openAccordion === 'completed' && (
            <div className="border-t border-gray-200">
              {completedPlans.length === 0 ? (
                <div className="p-4 text-center">
                  <CheckCircleIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <h3 className="text-sm font-medium text-gray-900">No past plans</h3>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {completedPlans
                    .sort((a, b) => new Date(b.completedDate || b.endDate).getTime() - new Date(a.completedDate || a.endDate).getTime())
                    .map(plan => (
                      <div
                        key={plan.id}
                        className="p-3 hover:bg-gray-50 cursor-pointer transition-colors opacity-75"
                        onClick={() => navigate(`/client/meals/${plan.id}`)}
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900 text-sm">{plan.name}</h4>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">
                              {new Date(plan.completedDate || plan.endDate).toLocaleDateString()}
                            </span>
                            <CheckCircleIcon className="h-4 w-4 text-green-500" />
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientMeals;
